import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { register as registerApi } from '../../api/auth';
import { useAuth } from '../../auth/AuthContext';
import { Card } from '../../shared/components/Card';
import { Field, Input } from '../../shared/components/Field';
import { PasswordInput } from '../../shared/components/PasswordInput';
import { Button } from '../../shared/components/Button';
import { Alert, Spinner } from '../../shared/components/feedback';
import { ApiError } from '../../api/client';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    zipCode: '',
    address: '',
    addressDetail: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const update =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await registerApi(form);
      // 가입 성공 시 바로 로그인 처리
      await login({ email: form.email, password: form.password });
      navigate('/account', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : '회원가입에 실패했습니다. 입력값을 확인해 주세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Title>회원가입</Title>
      <Subtitle>계정을 만든 뒤 Threads를 연결하면 준비가 끝납니다.</Subtitle>

      <Form onSubmit={handleSubmit} aria-busy={loading}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="이메일" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="you@example.com"
            value={form.email}
            onChange={update('email')}
            required
          />
        </Field>

        <Field label="비밀번호" htmlFor="password" help="8자 이상 입력해 주세요.">
          <PasswordInput
            id="password"
            minLength={8}
            autoComplete="new-password"
            placeholder="8자 이상 입력"
            value={form.password}
            onChange={update('password')}
            required
          />
        </Field>

        <Row>
          <Field label="이름" htmlFor="name">
            <Input
              id="name"
              autoComplete="name"
              placeholder="홍길동"
              value={form.name}
              onChange={update('name')}
              required
            />
          </Field>
          <Field label="휴대폰" htmlFor="phone">
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="010-1234-5678"
              value={form.phone}
              onChange={update('phone')}
              required
            />
          </Field>
        </Row>

        <OptionalFields>
          <summary>주소 추가 <span>선택사항</span></summary>
          <OptionalBody>
        <Row>
          <Field label="우편번호 (선택)" htmlFor="zipCode">
            <Input
              id="zipCode"
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
              value={form.zipCode}
              onChange={update('zipCode')}
            />
          </Field>
          <Field label="주소 (선택)" htmlFor="address">
            <Input
              id="address"
              autoComplete="address-line1"
              placeholder="서울시 ..."
              value={form.address}
              onChange={update('address')}
            />
          </Field>
        </Row>

        <Field label="상세 주소 (선택)" htmlFor="addressDetail">
          <Input
            id="addressDetail"
            autoComplete="address-line2"
            placeholder="101동 1001호"
            value={form.addressDetail}
            onChange={update('addressDetail')}
          />
        </Field>
          </OptionalBody>
        </OptionalFields>

        <Button type="submit" $size="lg" $fullWidth disabled={loading}>
          {loading ? <><Spinner aria-hidden="true" /> 계정 만드는 중…</> : '가입하고 시작하기'}
        </Button>
      </Form>

      <Footer>
        이미 계정이 있으신가요? <Link to="/login">로그인</Link>
      </Footer>
      <LegalLinks>
        <LegalLink to="/privacy">개인정보 처리방침</LegalLink>
        <span aria-hidden>·</span>
        <LegalLink to="/data-deletion">데이터 삭제</LegalLink>
      </LegalLinks>
    </Card>
  );
}

const Title = styled.h1`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin-top: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Form = styled.form`
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OptionalFields = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  summary {
    padding: 12px 14px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    min-height: 44px;
  }
  summary span {
    margin-left: 8px;
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const OptionalBody = styled.div`
  display: grid;
  gap: 16px;
  padding: 4px 14px 16px;
`;

const Footer = styled.p`
  margin-top: 20px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const LegalLinks = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LegalLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textMuted};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;
